import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router";

import { MapEditorObservationPanel } from "~/modules/mapEditor/components/MapEditorObservationPanel";
import { MapEditorObservationPanelEcologicalFunctionFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/components/EcologicalFunctionCard/fragments.generated";
import { MapEditorObservationPanelObservationMediaPointFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/components/ObservationMediaPointCard/fragments.generated";

import { ProjectReviewVisitV2Screen_GetDataDocument } from "../../../../queries.generated";

interface ProjectReviewVisitV2PossibleFunctionObservationDetailRouteProps {
    backHref: string;
}

export function ProjectReviewVisitV2PossibleFunctionObservationDetailRoute({
    backHref,
}: ProjectReviewVisitV2PossibleFunctionObservationDetailRouteProps) {
    const { t } = useTranslation("project");
    const { mapEditorFeatureId, visitId, clientSlug, projectSlug } = useParams();
    const navigate = useNavigate();

    const client = useApolloClient();

    if (!visitId || !mapEditorFeatureId || !clientSlug || !projectSlug) {
        throw new Error("Parameters `visitId`, `mapEditorFeatureId`, `clientSlug`, and `projectSlug` are required");
    }

    const createLinkToObservationMediaCard = (element: MapEditorObservationPanelObservationMediaPointFragment) => {
        return generatePath("/c/:clientSlug/p/:projectSlug/map-editor/:layerId/:featureId", {
            clientSlug: clientSlug,
            projectSlug: projectSlug,
            layerId: element.feature.layerId,
            featureId: element.mapEditorFeatureId,
        });
    };

    const createLinkToEcologicalFunction = (element: MapEditorObservationPanelEcologicalFunctionFragment) => {
        return generatePath("/c/:clientSlug/p/:projectSlug/review-visit/:visitId/functions/created/:featureId", {
            clientSlug: clientSlug,
            projectSlug: projectSlug,
            visitId: visitId,
            featureId: element.featureId,
        });
    };

    const handleAfterDelete = async () => {
        await client.refetchQueries({ include: [ProjectReviewVisitV2Screen_GetDataDocument] });
    };

    return (
        <MapEditorObservationPanel
            backHref={backHref}
            createHrefToEcologicalFunction={createLinkToEcologicalFunction}
            createHrefToObservationMediaPointCard={createLinkToObservationMediaCard}
            mapEditorFeatureId={mapEditorFeatureId}
            onEcologicalFunctionCreated={element => {
                navigate(
                    generatePath(
                        "/c/:clientSlug/p/:projectSlug/review-visit/:visitId/functions/possible/:mapEditorFeatureId/created/:featureId",
                        {
                            clientSlug,
                            projectSlug,
                            visitId,
                            mapEditorFeatureId,
                            featureId: element.feature.id,
                        },
                    ),
                );
            }}
            onObservationDeleted={handleAfterDelete}
            readonly={false}
            title={t("projectReviewVisitV2.functionsRoute.possibleFunctionObservationDetailTitle")}
        />
    );
}
